/**
 * Authentication API Service
 * 
 * Handles authentication via Auth0 OAuth (BFF Pattern).
 * NO tokens are stored in frontend - only session_id in HttpOnly cookie.
 */

import { apiClient } from "./apiClient";
import { getApiBase } from "./apiConfig";

export interface User {
  user_id: string;
  email: string;
  name: string;
  picture?: string;
  role?: string;
}

/**
 * Get current authenticated user info.
 * Returns user if authenticated, throws error if not.
 */
export async function getCurrentUser(): Promise<User> {
  const response = await apiClient.get<User>("/auth/me");
  return response.data;
}

/**
 * Get current authenticated user info (optional).
 * Returns user if authenticated, null if not.
 * Does not throw errors - useful for public pages.
 */
export async function getCurrentUserOptional(): Promise<User | null> {
  try {
    const response = await apiClient.get<User | null>("/auth/me-optional");
    return response.data;
  } catch (error) {
    // Return null on any error (including 401)
    return null;
  }
}

/**
 * Validate returnUrl to prevent open redirect attacks.
 * Only allows relative paths or URLs from the same origin.
 */
function validateReturnUrl(url: string): string {
  if (!url) {
    return "";
  }

  try {
    const parsed = new URL(url, window.location.origin);
    // Only allow relative paths or same-origin URLs
    if (parsed.origin === window.location.origin || url.startsWith("/")) {
      return url;
    }
    // Reject external URLs - return empty to use default
    console.warn("Blocked external returnUrl:", url);
    return "";
  } catch {
    // If URL parsing fails, treat as relative path
    return url.startsWith("/") ? url : "";
  }
}

/**
 * Initiate login flow.
 * Redirects to Auth0 login page.
 *
 * Note: For redirects (window.location.href), we need full URL to backend.
 * Proxy only works for fetch/axios requests, not browser redirects.
 */
export function login(returnUrl?: string): void {
  const safeReturnUrl = validateReturnUrl(returnUrl || "");
  const returnUrlParam = safeReturnUrl
    ? `?return_url=${encodeURIComponent(safeReturnUrl)}`
    : "";

  // For redirects, we need full backend URL (not proxy)
  // Proxy only works for fetch/axios, not browser redirects
  const apiBase = getApiBase();
  let loginUrl: string;

  if (!apiBase || apiBase === "") {
    // Same-origin deployment (nginx proxy): use relative URL
    loginUrl = `/api/auth/login${returnUrlParam}`;
  } else {
    // Production: use configured API base
    loginUrl = `${apiBase}/api/auth/login${returnUrlParam}`;
  }

  window.location.href = loginUrl;
}

/**
 * Logout user.
 * Clears session and redirects to Auth0 logout.
 * 
 * Note: For redirects (window.location.href), we need full URL to backend.
 * Proxy only works for fetch/axios requests, not browser redirects.
 */
export function logout(): void {
  // For redirects, we need full backend URL (not proxy)
  // Proxy only works for fetch/axios, not browser redirects
  const apiBase = getApiBase();
  let logoutUrl: string;
  
  if (!apiBase || apiBase === "") {
    // Same-origin deployment (nginx proxy): use relative URL
    logoutUrl = `/api/auth/logout`;
  } else {
    // Production: use configured API base
    logoutUrl = `${apiBase}/api/auth/logout`;
  }
  
  window.location.href = logoutUrl;
}

